import React from 'react';
import { Box, Typography, Button, Grid, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import GradientText from '../../../components/common/GradientText.tsx';
import { MicNone as MicIcon, ArrowForward, TrendingUp, Lightbulb, Business, Settings } from '@mui/icons-material';

// Define user progress states for type safety
type UserProgressState = 'initial' | 'input' | 'submitted' | 'selected';
type SelectedSection = 'capital' | 'operations' | 'similar' | 'toolbox' | null;

interface HeroSectionProps {
  onGetStarted: () => void;
  userProgress: UserProgressState;
  selectedSection: SelectedSection;
  problemStatement: string;
}

const HeroContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(6, 2),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 12,
    left: 0,
    width: '4px',
    height: '96%',
    background: 'linear-gradient(to bottom, #00CC99,  #8A2BE2)',
  },
}));

const Headline = styled(Typography)(({ theme }) => ({
  fontSize: '3.5rem',
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
}));

const Subheadline = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  color: 'rgba(255, 255, 255, 0.8)',
  maxWidth: '800px',
  margin: '0 auto',
  marginBottom: theme.spacing(4),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '30px',
  padding: theme.spacing(1.5, 4),
  fontSize: '1rem',
  fontWeight: 600,
  boxShadow: '0 10px 20px rgba(138, 43, 226, 0.3)',
  background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
  '&:hover': {
    background: 'linear-gradient(45deg, #9370DB 30%, #8A2BE2 90%)',
    boxShadow: '0 10px 30px rgba(138, 43, 226, 0.5)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.3s ease',
}));

const VoiceButton = styled(Button)(({ theme }) => ({
  borderRadius: '30px',
  padding: theme.spacing(1.5, 4),
  fontSize: '1rem',
  fontWeight: 600,
  marginLeft: theme.spacing(2),
  boxShadow: '0 10px 20px rgba(0, 204, 153, 0.3)',
  background: 'linear-gradient(45deg, #00CC99 30%, #00FFCC 90%)',
  '&:hover': {
    background: 'linear-gradient(45deg, #00FFCC 30%, #00CC99 90%)',
    boxShadow: '0 10px 30px rgba(0, 204, 153, 0.5)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.3s ease',
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  background: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(138, 43, 226, 0.2)',
    border: '1px solid rgba(138, 43, 226, 0.5)',
  },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  background: 'rgba(138, 43, 226, 0.2)',
  color: '#fff',
}));

const ProblemSummary = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'rgba(0, 51, 102, 0.3)',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(4),
  border: '1px solid rgba(0, 204, 153, 0.3)',
}));

const HeroSection: React.FC<HeroSectionProps> = ({ 
  onGetStarted, 
  userProgress, 
  selectedSection,
  problemStatement 
}) => {
  // Render different content based on user progress
  const renderContent = () => {
    switch (userProgress) {
      case 'initial':
        return renderInitialState();
      case 'input':
        return renderInputState();
      case 'submitted':
        return renderSubmittedState();
      case 'selected':
        return renderSelectedState();
      default:
        return renderInitialState();
    }
  };

  // Initial welcome state
  const renderInitialState = () => (
    <>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Headline>
          <GradientText gradient="primary" variant="h1">
            Welcome to the Problem-Solving Nexus!
          </GradientText>
          <GradientText gradient="secondary" variant="h3">
            Solve Complex Problems
          </GradientText>
        </Headline>
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Subheadline>
          This is your AI-powered hub for identifying, refining, and solving challenges effectively. 
          <br /> Get started by:
          <br /> 1. Creating or refining a problem statement
          <br /> 2. Exploring solutions from similar challenges
          <br /> 3. Engaging with AI-driven executive surrogates for expert insights
         </Subheadline>
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <ActionButton 
          variant="contained" 
          color="primary"
          onClick={onGetStarted}
        >
          Get Started
        </ActionButton>
        
        <VoiceButton
          variant="contained"
          color="secondary"
          startIcon={<MicIcon />}
          onClick={onGetStarted}
        >
          Voice Input
        </VoiceButton>
      </motion.div>
    </>
  );

  // Input state - when user is entering their problem
  const renderInputState = () => (
    <>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Headline>
          <GradientText gradient="primary" variant="h1">
            Describe Your Problem
          </GradientText>
        </Headline>
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Subheadline>
          Be specific and include relevant details to get the best solutions from our executive surrogates. The more context you provide, the better the solutions will be.
        </Subheadline>
      </motion.div>
    </>
  );

  // Submitted state - after user has submitted their problem
  const renderSubmittedState = () => (
    <>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Headline>
          <GradientText gradient="primary" variant="h2">
            Problem Analysis
          </GradientText>
        </Headline>
      </motion.div>
      
      {problemStatement && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <ProblemSummary>
            <Typography variant="subtitle2" sx={{ color: 'rgba(0, 204, 153, 0.9)', mb: 1 }}>
              Your Problem:
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              {problemStatement}
            </Typography>
          </ProblemSummary>
        </motion.div>
      )}
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Subheadline>
          Our executive surrogates are analyzing your problem. Explore the different perspectives and solutions below.
        </Subheadline>
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <SectionCard onClick={() => {}}>
              <IconContainer>
                <Business fontSize="large" />
              </IconContainer>
              <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                Capital Focus
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Financial and investment perspectives
              </Typography>
            </SectionCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SectionCard onClick={() => {}}>
              <IconContainer>
                <Settings fontSize="large" />
              </IconContainer>
              <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                Operations Focus
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Process and efficiency insights
              </Typography>
            </SectionCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SectionCard onClick={() => {}}>
              <IconContainer>
                <Lightbulb fontSize="large" />
              </IconContainer>
              <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                Similar Problems
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Learn from related challenges
              </Typography>
            </SectionCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SectionCard onClick={() => {}}>
              <IconContainer>
                <TrendingUp fontSize="large" />
              </IconContainer>
              <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                Problem Solving
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Tools and methodologies
              </Typography>
            </SectionCard>
          </Grid>
        </Grid>
      </motion.div>
    </>
  );

  // Selected state - when user has selected a specific section
  const renderSelectedState = () => {
    let title = '';
    let description = '';
    let icon = <ArrowForward fontSize="large" />;

    switch (selectedSection) {
      case 'capital':
        title = 'Capital Focus';
        description = 'Financial and investment perspectives on your problem';
        icon = <Business fontSize="large" />;
        break;
      case 'operations':
        title = 'Operations Focus';
        description = 'Process and efficiency insights for your challenge';
        icon = <Settings fontSize="large" />;
        break;
      case 'similar':
        title = 'Similar Problems';
        description = 'Learn from related challenges and their solutions';
        icon = <Lightbulb fontSize="large" />;
        break;
      case 'toolbox':
        title = 'Problem Solving Toolbox';
        description = 'Tools and methodologies to address your problem';
        icon = <TrendingUp fontSize="large" />;
        break;
      default:
        title = 'Problem Analysis';
        description = 'Comprehensive analysis of your challenge';
        // Default icon is already set
        break;
    }

    return (
      <>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <IconContainer>
              {icon}
            </IconContainer>
          </Box>
          <Headline>
            <GradientText gradient="primary" variant="h2">
              {title}
            </GradientText>
          </Headline>
        </motion.div>
        
        {problemStatement && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <ProblemSummary>
              <Typography variant="subtitle2" sx={{ color: 'rgba(0, 204, 153, 0.9)', mb: 1 }}>
                Your Problem:
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                {problemStatement}
              </Typography>
            </ProblemSummary>
          </motion.div>
        )}
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Subheadline>
            {description}
          </Subheadline>
        </motion.div>
      </>
    );
  };

  return (
    <HeroContainer>
      {renderContent()}
    </HeroContainer>
  );
};

export default HeroSection; 